import { BookingStepSkeleton } from '@components/BookingFlow/Common/BookingStepSkeleton';
import { t } from '@lingui/core/macro';
import type { CarsWithSalesItemsForBookingResponseRecord } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { BookingAddress, SALES_ITEM_BOOKING_FLOW_TYPE } from 'noddi-types';
import { compareLicensePlates, groupCarsWithQuantity } from 'noddi-util';
import {
  log,
  useBookingAddress,
  useBookingCars,
  useBookingSelectedSalesItemsCars,
  useIsMultipleCarsBooking,
  useLocalizationLanguage
} from 'noddi-web';
import { useEffect } from 'react';
import { useMobileBookingCustomer } from '../../../../hooks/useMobileBookingCustomer';
import { useNextStepAfterServicesPath } from '../../../../hooks/useNextStepAfterServicesPath';
import { useCurrentBrand } from '../../../../stores/BrandStore/brandStore';

import { getMobileBookingProgress } from '@components/BookingFlow/Common/bookingFlowProgress';
import { BookingStepUnavailablePage } from '@components/BookingFlow/Common/BookingStepUnavailablePage';
import { BookingScreen } from '@components/BookingFlow/Mobile/BookingScreen';
import { ApiErrorMessageWithTrans } from '@components/Error/ApiErrorMessageWithTrans';
import { NoddiInfoBanner } from 'noddi-ui-web';
import { useNavigate } from 'react-router-dom';
import { SalesItemsMultipleCarsSelector } from '../Services/SalesItemsMultipleCarsSelector';
import { CarWithAddons } from './CarWithAddons';

export const Addons = () => {
  const navigate = useNavigate();
  const nextStepAfterServicesPath = useNextStepAfterServicesPath();

  const selectedAddress = useBookingAddress();

  if (!selectedAddress) {
    return <BookingStepUnavailablePage missing='address' />;
  }

  return (
    <BookingScreen
      progress={getMobileBookingProgress('services')}
      onNextClicked={() => {
        navigate(nextStepAfterServicesPath);
      }}
      title={t`Do you need help with anything else?`}
    >
      <AddonsSelector selectedAddress={selectedAddress} />
    </BookingScreen>
  );
};

const AddonsSelector = ({ selectedAddress }: { selectedAddress: BookingAddress }) => {
  const selectedSalesItemsCars = useBookingSelectedSalesItemsCars();
  const isMultipleCarsBooking = useIsMultipleCarsBooking();
  const { userGroupId: currentUserGroupId } = useMobileBookingCustomer();
  const { currentBrand } = useCurrentBrand();
  const language = useLocalizationLanguage();

  const {
    data: carsWithAddons,
    isPending: isCarsWithAddonsPending,
    error: carsWithAddonsError
  } = noddiAsync.usePostAsGetQuery({
    type: URLKeys.postFetchAddons,
    input: {
      addressId: selectedAddress.id,
      // To avoid refetch of the same addons when the addon selection changes, we only pass the primary sales items
      cars: groupCarsWithQuantity(
        selectedSalesItemsCars.filter((car) => car.type === SALES_ITEM_BOOKING_FLOW_TYPE.PRIMARY)
      ),
      userGroupId: currentUserGroupId,
      brandDomain: currentBrand,
      language
    },
    queryConfig: {
      staleTime: Infinity
    }
  });

  const hasEmptyCars = carsWithAddons?.data.cars.length === 0;
  useEffect(() => {
    if (hasEmptyCars) {
      log({
        value: 'Addons catalog returned no cars',
        severity: 'error',
        extra: { queryParams: { addressId: selectedAddress.id } }
      });
    }
  }, [hasEmptyCars, selectedAddress.id]);

  if (isCarsWithAddonsPending) {
    return <BookingStepSkeleton />;
  }
  if (carsWithAddonsError) {
    return <ApiErrorMessageWithTrans error={carsWithAddonsError} />;
  }

  return (
    <div className='flex flex-col gap-6'>
      {isMultipleCarsBooking ? (
        <SalesItemsMultipleCarsSelector
          availableSalesItems={carsWithAddons.data}
          salesItemType={SALES_ITEM_BOOKING_FLOW_TYPE.ADDON}
        />
      ) : (
        <SingleCarAddonsSelector availableSalesItems={carsWithAddons.data} />
      )}
    </div>
  );
};

const SingleCarAddonsSelector = ({
  availableSalesItems
}: {
  availableSalesItems: CarsWithSalesItemsForBookingResponseRecord;
}) => {
  const bookingCars = useBookingCars();

  // Pair each returned car with the one in the draft, so the picker gets a car it can count on.
  const carsInBooking = availableSalesItems.cars.flatMap((carWithAddons) => {
    const car = bookingCars.find((bookingCar) =>
      compareLicensePlates(bookingCar.licensePlate, carWithAddons.licensePlate)
    );
    return car ? [{ carWithAddons, car }] : [];
  });

  // Dropping every unpaired car can empty the list — say so rather than rendering blank step chrome.
  if (carsInBooking.length === 0) {
    return (
      <NoddiInfoBanner texts={[{ value: t`We don't have any addons available for the services you've selected` }]} />
    );
  }

  return (
    <>
      {carsInBooking.map(({ carWithAddons, car }) => (
        <CarWithAddons key={carWithAddons.licensePlate.number} carWithAddons={carWithAddons} car={car} />
      ))}
    </>
  );
};
